/*
 * Offline write interceptor for axios.
 *
 * Responsibilities:
 *   1. Intercept POST/PUT/DELETE requests to a configured allow-list of paths.
 *   2. When the device is offline, persist the request to IndexedDB and resolve
 *      with a *synthesised* success response so the calling component can
 *      update its UI optimistically.
 *   3. When the device comes back online, drain the queue in FIFO order, mapping
 *      any optimistic IDs to the real server IDs returned by POST responses.
 *   4. Detect server-side conflicts (HTTP 409) and surface them to a registered
 *      conflict handler so a UI can prompt the user to keep theirs or yours.
 *
 * Why bake this into axios instead of a custom HTTP wrapper?
 *   The codebase calls axios.* directly from dozens of places. Wrapping at the
 *   axios layer means we don't have to refactor a single call site to get
 *   offline support — components just keep doing what they already do.
 */

import axios from 'axios';
import {
  enqueueOp,
  removeOp,
  updateOp,
  listOps,
  recordMapping,
  resolveMapping,
  queueSize,
  putReadCache,
  getReadCache,
  listReadCacheKeys,
} from './offlineQueueDb';
import { isOnline } from './serviceWorker';
import { isLikelyOffline, markNetworkFailure, markNetworkSuccess, isNetworkError, onNetworkRecovered } from './networkStatus';

// Path prefixes whose mutations are safe to queue offline. Anything else
// (auth, payment, admin, voice transcription, AI) bypasses the queue entirely.
const QUEUABLE_PREFIXES = [
  '/api/tasks',
  '/api/contacts',
  '/api/categories',
];

// Suffixes that should never be queued even if the prefix matches — these are
// upload endpoints or RPC-style endpoints that need fresh round-trips.
const NEVER_QUEUE_SUFFIXES = [
  '/attachment',
  '/check-conflicts',
  '/bulk-delete',
  '/import',
];

const matchesQueuable = (path) =>
  QUEUABLE_PREFIXES.some((p) => path.startsWith(p)) &&
  !NEVER_QUEUE_SUFFIXES.some((s) => path.includes(s));

// GET paths whose responses should be cached for offline read access.
// These are the same as the write-queueable prefixes plus a couple of
// account/profile endpoints whose data the UI relies on at startup.
const CACHEABLE_READ_PREFIXES = [
  '/api/tasks',
  '/api/contacts',
  '/api/categories',
  '/api/profile',
  '/api/usage',
  '/api/auth/me',
];

// Read endpoints we never want to cache (RPC-style, dynamic counts, large
// blobs, conflict probes, etc.) — even when they sit under a cached prefix.
const NEVER_CACHE_READ_SUFFIXES = [
  '/check-conflicts',
  '/bulk-delete',
  '/attachment',
  '/export',
  '/import',
];

const matchesReadCache = (path) =>
  CACHEABLE_READ_PREFIXES.some((p) => path.startsWith(p)) &&
  !NEVER_CACHE_READ_SUFFIXES.some((s) => path.includes(s));

// Build a stable cache key from a URL — pathname plus the canonical search
// string. We deliberately ignore origin so the same key works whether the
// API is on the same host as the frontend or on a separate domain.
const cacheKeyForUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    const params = Array.from(u.searchParams.entries()).sort(([a], [b]) => a.localeCompare(b));
    const search = params.length ? `?${params.map(([k, v]) => `${k}=${v}`).join('&')}` : '';
    return `${u.pathname}${search}`;
  } catch {
    return url;
  }
};

// Try to derive a single-entity response from a previously cached list. The
// UI commonly does GET /api/tasks/:id after the list has loaded, so when
// offline we can synthesise that response from the list cache.
const synthesiseEntityFromList = async (path) => {
  // Match /api/<kind>s/<id> exactly (not /api/<kind>s/<id>/sub-resource).
  const m = path.match(/^\/api\/(tasks|contacts|categories)\/([^/]+)$/);
  if (!m) return null;
  const kind = m[1];
  const id = m[2];
  // Look up any cached list for that kind (we may have cached multiple
  // pages or filtered versions — first match wins, callers can refresh
  // once back online).
  const entries = await listReadCacheKeys(`/api/${kind}`);
  for (const entry of entries) {
    const data = entry.data;
    const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : null;
    if (!items) continue;
    const match = items.find((it) => it && (it.id === id || it._id === id));
    if (match) return match;
  }
  return null;
};

// Apply an offline mutation to every cached list response for that kind so
// the user keeps seeing a consistent view across navigations / reloads.
const applyOptimisticToReadCache = async ({ method, path, body, kind, optimisticId }) => {
  if (!kind) return;
  const listPrefix = `/api/${kind}s`;
  const entries = await listReadCacheKeys(listPrefix);
  for (const entry of entries) {
    const key = entry.cache_key;
    // Only mutate list endpoints, not single-entity caches.
    if (key !== listPrefix && !key.startsWith(`${listPrefix}?`)) continue;
    const data = entry.data;
    const isWrapped = data && Array.isArray(data.items);
    const items = isWrapped ? data.items : Array.isArray(data) ? data : null;
    if (!items) continue;
    let newItems = items;
    if (method === 'POST') {
      const optimistic = {
        id: optimisticId,
        ...(body || {}),
        created_at: new Date().toISOString(),
        _offline: true,
      };
      newItems = [optimistic, ...items];
    } else if (method === 'PUT') {
      const targetId = extractEntityId(path, kind);
      if (!targetId) continue;
      newItems = items.map((it) =>
        it && (it.id === targetId || it._id === targetId)
          ? { ...it, ...(body || {}), _offline: true, updated_at: new Date().toISOString() }
          : it,
      );
    } else if (method === 'DELETE') {
      const targetId = extractEntityId(path, kind);
      if (!targetId) continue;
      newItems = items.filter((it) => it && it.id !== targetId && it._id !== targetId);
    } else {
      continue;
    }
    const newData = isWrapped ? { ...data, items: newItems } : newItems;
    await putReadCache(key, newData);
  }
};

const entityKindForPath = (path) => {
  if (path.startsWith('/api/tasks')) return 'task';
  if (path.startsWith('/api/contacts')) return 'contact';
  if (path.startsWith('/api/categories')) return 'category';
  return null;
};

// Pub-sub for things that care about queue state (UI counters, banners).
const queueListeners = new Set();
const notifyQueueChanged = async () => {
  let size = 0;
  try { size = await queueSize(); } catch { size = 0; }
  queueListeners.forEach((cb) => {
    try { cb(size); } catch (err) { console.error('Queue listener threw:', err); }
  });
};
export const subscribeQueueSize = (cb) => {
  queueListeners.add(cb);
  notifyQueueChanged();
  return () => queueListeners.delete(cb);
};

// Conflict handler — a single registered function that receives a conflict
// descriptor when the server returns 409 during drain. The handler is expected
// to surface UI and call back with the user's resolution.
let conflictHandler = null;
export const registerConflictHandler = (handler) => {
  conflictHandler = handler;
};

// --- ID utilities -----------------------------------------------------------

const newClientId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers: timestamp + random.
  return `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const extractPathname = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
};

// Compute the path with any optimistic ID swapped for the resolved server ID
// from a previous queued POST. Used both to build a synthesised offline
// response and at drain time.
const swapOptimisticIdInUrl = async (url, optimisticIdHint) => {
  if (!optimisticIdHint) return url;
  const mapped = await resolveMapping(optimisticIdHint);
  if (!mapped) return url;
  return url.replace(optimisticIdHint, mapped);
};

// Extract the entity ID segment that follows /api/<kind>/ (if any).
const extractEntityId = (path, kind) => {
  if (!kind) return null;
  const m = path.match(new RegExp(`^/api/${kind}s/([^/]+)`));
  return m ? m[1] : null;
};

// --- Synthesised offline responses -----------------------------------------

const synthOfflineResponse = (method, path, body, optimisticId) => {
  // POST → return body merged with an optimistic ID + offline marker.
  if (method === 'POST') {
    const optimistic = {
      id: optimisticId,
      ...body,
      created_at: new Date().toISOString(),
      _offline: true,
    };
    return { data: optimistic, status: 202, statusText: 'Queued (offline)' };
  }
  // PUT → return updated body.
  if (method === 'PUT') {
    return {
      data: { ...body, _offline: true, updated_at: new Date().toISOString() },
      status: 202,
      statusText: 'Queued (offline)',
    };
  }
  // DELETE → return a success-ish payload.
  if (method === 'DELETE') {
    return { data: { message: 'queued', _offline: true }, status: 202, statusText: 'Queued (offline)' };
  }
  // Fallback (shouldn't hit).
  return { data: { _offline: true }, status: 202, statusText: 'Queued (offline)' };
};

// --- Drain -----------------------------------------------------------------

let draining = false;
let drainRequested = false;
const drainListeners = new Set();
export const subscribeDrainEvents = (cb) => {
  drainListeners.add(cb);
  return () => drainListeners.delete(cb);
};
const emitDrainEvent = (event) => {
  drainListeners.forEach((cb) => {
    try { cb(event); } catch (err) { console.error('Drain listener threw:', err); }
  });
};

const replayOp = async (op) => {
  // Translate optimistic IDs anywhere in the URL.
  const newUrl = await swapOptimisticIdInUrl(op.url, op.optimistic_id);

  // For PUT/DELETE that target an entity that was originally POST'd offline,
  // the body might also contain the optimistic ID - rare but cheap to handle.
  const body = op.body;

  try {
    const headers = { 'X-Edotask-Offline-Replay': '1' };
    // Always re-read the auth token from localStorage on replay — the user may
    // have been logged out and back in between the queue and the drain, so we
    // can't rely on axios.defaults from the original request.
    try {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch { /* noop */ }
    // If we have an optimistic_updated_at, send it so the server can reject
    // on conflict (server may ignore today; we still mark intent).
    if (op.snapshot_updated_at) {
      headers['X-Edotask-If-Unmodified-Since'] = op.snapshot_updated_at;
    }
    const res = await axios({
      method: op.method,
      url: newUrl,
      data: body,
      headers,
    });

    // For POST responses with a `.id`, record the optimistic→server mapping.
    if (op.method === 'POST' && op.optimistic_id && res.data && res.data.id) {
      await recordMapping(op.optimistic_id, res.data.id);
    }
    return { ok: true, response: res };
  } catch (err) {
    return { ok: false, error: err, response: err.response };
  }
};

export const drainQueue = async () => {
  if (draining) {
    drainRequested = true;
    return;
  }
  if (!isOnline()) return;

  draining = true;
  emitDrainEvent({ type: 'drain_started' });
  try {
    let ops = [];
    try { ops = await listOps(); } catch { ops = []; }

    for (const op of ops) {
      if (!isOnline()) break; // bail mid-drain if network dropped again
      const result = await replayOp(op);
      if (result.ok) {
        await removeOp(op.op_id);
        emitDrainEvent({ type: 'op_synced', op, response: result.response.data });
      } else {
        const status = result.response?.status;
        if (status === 409 && conflictHandler) {
          // Hand off to the registered UI handler. Possible resolutions:
          //   'keep_mine'  → retry with X-Edotask-Force-Overwrite: 1
          //   'keep_theirs' → discard the op
          //   'skip'        → leave for later (default if no handler)
          let resolution = 'skip';
          try {
            resolution = await conflictHandler({
              op,
              serverResponse: result.response?.data,
            });
          } catch (e) {
            console.error('Conflict handler threw:', e);
          }
          if (resolution === 'keep_theirs') {
            await removeOp(op.op_id);
            emitDrainEvent({ type: 'op_discarded', op, reason: 'kept_theirs' });
          } else if (resolution === 'keep_mine') {
            try {
              await axios({
                method: op.method,
                url: await swapOptimisticIdInUrl(op.url, op.optimistic_id),
                data: op.body,
                headers: {
                  'X-Edotask-Offline-Replay': '1',
                  'X-Edotask-Force-Overwrite': '1',
                },
              });
              await removeOp(op.op_id);
              emitDrainEvent({ type: 'op_synced', op, response: null });
            } catch (e) {
              // Keep in queue for another try.
              const updated = { ...op, attempts: (op.attempts || 0) + 1, last_error: String(e) };
              await updateOp(updated);
              emitDrainEvent({ type: 'op_error', op: updated });
            }
          } else {
            const updated = { ...op, attempts: (op.attempts || 0) + 1, last_error: 'conflict_unresolved' };
            await updateOp(updated);
          }
        } else if (status === 401 || status === 403) {
          // Auth issue — leave in queue so it retries after the user is logged
          // back in (App.js triggers a drain on token restore).
          const updated = { ...op, attempts: (op.attempts || 0) + 1, last_error: `auth_${status}` };
          await updateOp(updated);
          emitDrainEvent({ type: 'op_error', op: updated });
          break;
        } else if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Permanent client errors (validation, not-found) — drop the op so
          // we don't infinite-loop.
          await removeOp(op.op_id);
          emitDrainEvent({ type: 'op_failed', op, status });
        } else {
          // Network or 5xx — leave in queue for the next online event.
          const updated = { ...op, attempts: (op.attempts || 0) + 1, last_error: result.error?.message || 'unknown' };
          await updateOp(updated);
          emitDrainEvent({ type: 'op_error', op: updated });
          break; // stop the drain — likely offline again
        }
      }
    }
  } finally {
    draining = false;
    emitDrainEvent({ type: 'drain_finished' });
    await notifyQueueChanged();
    if (drainRequested) {
      drainRequested = false;
      // Coalesce repeated drain triggers.
      drainQueue();
    }
  }
};

// --- Axios installation ----------------------------------------------------

export const installOfflineInterceptor = () => {
  if (typeof window === 'undefined') return;

  // Drain whenever the device flips back online via OS-level events. Clear
  // the sticky-offline flag immediately so the very first replay request
  // doesn't get re-queued because of a recent pre-online failure.
  window.addEventListener('online', () => {
    markNetworkSuccess();
    drainQueue();
  });
  // Also drain whenever a successful request proves we're actually reachable
  // again (covers the case where navigator.onLine lies on mobile).
  onNetworkRecovered(() => { drainQueue(); });
  if (isOnline()) drainQueue();

  axios.interceptors.request.use(async (config) => {
    const method = (config.method || 'get').toUpperCase();

    // Drain replays must bypass the queue interceptor entirely — otherwise
    // the sticky-offline flag could cause us to re-queue an op while we're
    // actively trying to flush the queue.
    const replayHeader = config.headers && (config.headers['X-Edotask-Offline-Replay'] || config.headers['x-edotask-offline-replay']);
    if (replayHeader) {
      return config;
    }

    // Normalise pathname (handles full URLs and relative ones).
    const fullUrl = axios.getUri ? axios.getUri(config) : (config.url || '');
    const path = extractPathname(fullUrl);

    // ---- GET: serve from offline read cache when we're likely offline -----
    if (method === 'GET' || method === 'HEAD') {
      if (!matchesReadCache(path)) return config;
      if (!isLikelyOffline()) return config;
      // Offline + cacheable read: synthesise a 200 from IndexedDB if we have
      // it. If not, we still let the request proceed — it will fail with a
      // network error which the response interceptor will translate into a
      // cache lookup (or a clean error if nothing's cached).
      try {
        const cached = await getReadCache(cacheKeyForUrl(fullUrl));
        if (cached) {
          config.__offlineSynthetic = {
            data: cached.data,
            status: 200,
            statusText: 'OK (offline cache)',
            headers: { 'X-Edotask-Served-From-Cache': '1' },
          };
          config.adapter = () => Promise.reject({ __isOfflineSynthetic: true, config });
          return config;
        }
        // Special case: GET /api/<kind>/<id> when only the list is cached.
        const synth = await synthesiseEntityFromList(path);
        if (synth) {
          config.__offlineSynthetic = {
            data: synth,
            status: 200,
            statusText: 'OK (offline cache, derived)',
            headers: { 'X-Edotask-Served-From-Cache': '1' },
          };
          config.adapter = () => Promise.reject({ __isOfflineSynthetic: true, config });
          return config;
        }
      } catch (err) {
        console.warn('[offline] cache lookup failed:', err);
      }
      return config;
    }

    if (method === 'OPTIONS') return config;
    if (!matchesQueuable(path)) return config;

    // If we're online, also check the URL for any optimistic IDs and swap them
    // out so the server gets the real ID rather than a `tmp_...` we minted.
    if (!isLikelyOffline()) {
      const kind = entityKindForPath(path);
      const id = extractEntityId(path, kind);
      if (id && id.startsWith('tmp_')) {
        const mapped = await resolveMapping(id);
        if (mapped) {
          config.url = (config.url || '').replace(id, mapped);
        }
      }
      return config;
    }

    // ----- Offline path: queue and synthesise a response -----
    const body = config.data && typeof config.data === 'object' ? config.data : config.data;
    const kind = entityKindForPath(path);
    let optimisticId = null;
    if (method === 'POST') optimisticId = newClientId();
    else optimisticId = extractEntityId(path, kind);

    const op = {
      op_id: newClientId(),
      method,
      url: fullUrl,
      body: body ?? null,
      optimistic_id: optimisticId,
      entity_kind: kind,
      snapshot_updated_at: (body && body.updated_at) || null,
      queued_at: Date.now(),
      attempts: 0,
      last_error: null,
    };
    await enqueueOp(op);
    await notifyQueueChanged();

    // Keep the read cache in sync with the optimistic mutation so a page
    // reload while still offline shows the user's own changes (creates,
    // edits, deletes). Best-effort; failures here don't block the queue.
    try {
      await applyOptimisticToReadCache({ method, path, body, kind, optimisticId });
    } catch (e) {
      console.warn('[offline] optimistic cache update failed:', e);
    }

    // Return a "rejected" config that the response interceptor can short-circuit.
    config.__offlineSynthetic = synthOfflineResponse(method, path, body || {}, optimisticId);
    // Force the request to fail in a controlled way; we'll catch it below.
    // Setting adapter to throw a marker error keeps axios's flow intact.
    config.adapter = () => Promise.reject({ __isOfflineSynthetic: true, config });
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      // Any successful round-trip clears the sticky offline marker so the rest
      // of the app stops behaving as if we're offline.
      markNetworkSuccess();
      // Cache successful GET responses for offline read access.
      try {
        const method = (response.config?.method || 'get').toUpperCase();
        if ((method === 'GET') && response.status >= 200 && response.status < 300) {
          const fullUrl = axios.getUri ? axios.getUri(response.config) : (response.config?.url || '');
          const path = extractPathname(fullUrl);
          if (matchesReadCache(path) && response.data !== undefined) {
            putReadCache(cacheKeyForUrl(fullUrl), response.data).catch(() => {});
          }
        }
      } catch { /* noop — caching is best-effort */ }
      return response;
    },
    async (error) => {
      if (error && error.__isOfflineSynthetic) {
        const synth = error.config?.__offlineSynthetic;
        if (synth) {
          // Resolve as a success so callers don't see this as an error.
          return Promise.resolve({
            ...synth,
            headers: synth.headers || {},
            config: error.config,
            request: null,
          });
        }
      }
      // If the request died with a network-style error (no response, no DNS,
      // fetch aborted) — flag the app as "likely offline" so the next attempt
      // can short-circuit instead of timing out for 30 s. navigator.onLine
      // is unreliable on mobile, so we measure connectivity from real I/O.
      if (isNetworkError(error)) {
        markNetworkFailure();
        // For cacheable GETs, fall back to the IndexedDB read cache instead
        // of bubbling the network error up to the UI.
        try {
          const method = (error.config?.method || 'get').toUpperCase();
          if (method === 'GET' && error.config) {
            const fullUrl = axios.getUri ? axios.getUri(error.config) : (error.config.url || '');
            const path = extractPathname(fullUrl);
            if (matchesReadCache(path)) {
              const cached = await getReadCache(cacheKeyForUrl(fullUrl));
              if (cached) {
                return {
                  data: cached.data,
                  status: 200,
                  statusText: 'OK (offline cache, fallback)',
                  headers: { 'X-Edotask-Served-From-Cache': '1' },
                  config: error.config,
                  request: null,
                };
              }
              const synth = await synthesiseEntityFromList(path);
              if (synth) {
                return {
                  data: synth,
                  status: 200,
                  statusText: 'OK (offline cache, derived)',
                  headers: { 'X-Edotask-Served-From-Cache': '1' },
                  config: error.config,
                  request: null,
                };
              }
            }
          }
        } catch (e) {
          console.warn('[offline] fallback cache lookup failed:', e);
        }
      }
      return Promise.reject(error);
    },
  );
};
